import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import dayjs from "dayjs";

import { selectCurrentAllocation, useGetAccountSubscriptionsQuery, useGetSubscriptionTiersQuery } from "fond/api";
import { EXCEED_SUBSCRIPTION_QUOTA_KEY, EXPIRED_BANNER_KEY, NO_LICENSE_ADMIN, NO_LICENSE_MEMBER, SOON_TO_EXPIRED_BANNER_KEY } from "fond/constants";
import { useFeatureFlag } from "fond/featureFlags";
import { BillingCycle, Subscription, SubscriptionStatus } from "fond/types";

import { useGetActiveBillingCycle } from "./useGetActiveBillingCycle";
import { useGetCurrentActiveSubscription, useGetCurrentSubscription } from "./useGetCurrentSubscription";
import { useGetPendingBillingCycle } from "./useGetPendingBillingCycle";
import { useRoleCheck } from "./useRoleCheck";

const SOON_TO_EXPIRE_IN_DAYS = 14;

interface UseCanIPlannerReturn {
  canBuy: boolean;
  canUpgrade: boolean;
  hasSub: boolean;
}

export type BannerType = "soon to expire" | "expired" | "quota exceeded" | "admin without license" | "member without license";
interface useUserManagementBannerReturn {
  currentBillingCycle?: BillingCycle;
  showBanner: boolean;
  subscription?: Subscription;
  bannerType?: BannerType;
}

/**
 * Determine whether the user can buy or upgrade to the FOND Planner add-on
 *
 * Buying the add-on is only allowed when:
 * 1. The feature flag `subscription_model_uplift_epic` is enabled
 * 2. The current user is an admin of the account
 * 3. There is no existing subscription with the FOND Planner add-on
 *
 * Upgrading the add-on is only allowed when:
 * 1. The feature flag `subscription_model_uplift_epic` is enabled
 * 2. The current user is an admin of the account
 * 3. There is no pending billing cycle (i.e. a pending upgrade or downgrade)
 * 4. There exists a higher tier than the current tier
 */
export const useCanIPlanner = (): UseCanIPlannerReturn => {
  const { value: isCreateSubscriptionsEnabled } = useFeatureFlag("subscription_model_uplift_epic");
  const billingCycle = useGetActiveBillingCycle();
  const pendingBillingCycle = useGetPendingBillingCycle();
  const isAdmin = useRoleCheck("admin");

  const { data: tiers, isLoading: isTiersLoading } = useGetSubscriptionTiersQuery();

  const canBuy = isCreateSubscriptionsEnabled && isAdmin && !billingCycle?.Planner && !pendingBillingCycle;

  // Determine if the user can upgrade from their current tier
  const currentTier = tiers?.find((tier) => tier.ID === billingCycle?.Planner?.TierID);
  const canUpgradeFromCurrentTier = !isTiersLoading && tiers ? tiers.some((tier) => tier.TierLevel > (currentTier?.TierLevel || 0)) : false;
  const canUpgrade = !pendingBillingCycle && isCreateSubscriptionsEnabled && isAdmin && canUpgradeFromCurrentTier;
  const hasSub = !!(billingCycle || pendingBillingCycle);
  return { canBuy, canUpgrade, hasSub };
};

/**
 * Determine whether the user management banner is to be displayed
 */
export const useUserManagementBanner = (): useUserManagementBannerReturn => {
  const currentAllocation = useSelector(selectCurrentAllocation);
  const accountId = currentAllocation?.Account.ID;
  const { data: subscriptions } = useGetAccountSubscriptionsQuery(accountId ?? skipToken);
  const currentBillingCycle = useGetActiveBillingCycle();
  const activeSubscription = useGetCurrentActiveSubscription();
  const currentSubscription = useGetCurrentSubscription();

  // Do not show the banner if no subscription at all.
  if (!subscriptions || subscriptions.length === 0) return { showBanner: false };

  // Expire
  if (currentSubscription?.Status === SubscriptionStatus.Expired) {
    const showExpiredBanner = !sessionStorage.getItem(EXPIRED_BANNER_KEY);
    return { showBanner: showExpiredBanner, subscription: currentSubscription, bannerType: "expired" };
  }

  // Quota used up
  const NEARLY_EXHAUSTED_QUOTA_THRESHOLD = 0.9; // 90%
  if (
    activeSubscription?.Status === SubscriptionStatus.Active &&
    activeSubscription.Planner &&
    activeSubscription.Planner.QuotaUsage + (activeSubscription.DataPackUsage ?? 0) >=
      (activeSubscription.Planner.Quota + (activeSubscription.DataPackQuota ?? 0)) * NEARLY_EXHAUSTED_QUOTA_THRESHOLD
  ) {
    const showExceedQuotaBanner = !sessionStorage.getItem(EXCEED_SUBSCRIPTION_QUOTA_KEY);
    return { showBanner: showExceedQuotaBanner, subscription: activeSubscription, bannerType: "quota exceeded" };
  }

  // Soon to expire
  if (activeSubscription && dayjs(activeSubscription.EndTime).utc(true).diff(dayjs(), "days") <= SOON_TO_EXPIRE_IN_DAYS) {
    const showSoonToExpireBanner = !sessionStorage.getItem(SOON_TO_EXPIRED_BANNER_KEY);
    return { showBanner: showSoonToExpireBanner, subscription: currentSubscription, bannerType: "soon to expire", currentBillingCycle };
  }

  /**
   * User is an admin to an account with subscription, but does not have license
   */
  if (currentSubscription?.Status === SubscriptionStatus.Active && currentAllocation?.Role === "admin" && !currentAllocation.License) {
    return {
      showBanner: !sessionStorage.getItem(NO_LICENSE_ADMIN),
      subscription: currentSubscription,
      bannerType: "admin without license",
      currentBillingCycle,
    };
  }

  /**
   * User is a member to an account with subscription, but does not have license
   */
  if (currentSubscription?.Status === SubscriptionStatus.Active && currentAllocation?.Role === "member" && !currentAllocation.License) {
    return {
      showBanner: !sessionStorage.getItem(NO_LICENSE_MEMBER),
      subscription: currentSubscription,
      bannerType: "member without license",
      currentBillingCycle,
    };
  }

  return { showBanner: false };
};
